import { createFileRoute, getRouteApi, Link, useParams } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { ArrowLeft, Camera, CheckCircle2, CreditCard, Flag, Gavel, Heart, ShieldCheck, Star } from "lucide-react";
import { toast } from "sonner";
import {
  approveCompletion,
  getBookingDetail,
  reportIssue,
} from "@/lib/bookings.functions";
import { getCustomerAuctionSnapshot } from "@/lib/marketplace-live.functions";
import { getBookingReview, submitReview } from "@/lib/reviews.functions";
import { getCustomerPartnerMarketingConsent, getWouldHireAgain, setCustomerPartnerMarketingConsent, setWouldHireAgain } from "@/lib/repeat-customers.functions";
import { uploadPhoto } from "@/lib/uploads";
import { JobLocationMap } from "@/components/booking/JobLocationMap";
import { QuoteNegotiation } from "@/components/offers/QuoteNegotiation";
import { StickyJobActionBar } from "@/components/partner/StickyJobActionBar";
import { ShieldsLogo } from "@/components/brand/ShieldsLogo";
import { AppHeader } from "@/components/site/AppHeader";
import { ScheduleSection } from "@/components/booking/ScheduleSection";
import { StatusPill } from "@/components/StatusPill";
import { JobJourney } from "@/components/jobs/JobJourney";
import { useBookingRealtime } from "@/hooks/useBookingRealtime";
import { bookingStatusLabel, gbp, ukDate, ukDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const authLayout = getRouteApi("/_authenticated");

export const Route = createFileRoute("/_authenticated/bookings/$bookingId")({
  head: () => ({
    meta: [
      { title: "Booking — Shields" },
      { name: "description", content: "Booking detail, bids and timeline." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: BookingDetailPage,
});

function BookingDetailPage() {
  const { bookingId } = useParams({ from: "/_authenticated/bookings/$bookingId" });
  const { user } = authLayout.useRouteContext();
  const queryClient = useQueryClient();
  const { data, error, isLoading } = useQuery({
    queryKey: ["booking", bookingId],
    queryFn: () => getBookingDetail({ data: { bookingId } }),
    refetchInterval: 60_000,
  });
  const { data: liveAuction } = useQuery({
    queryKey: ["customer-auction", bookingId],
    queryFn: () => getCustomerAuctionSnapshot({ data: { bookingId } }),
    refetchInterval: 60_000,
  });
  const [busy, setBusy] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);

  async function refresh() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["booking", bookingId] }),
      queryClient.invalidateQueries({ queryKey: ["customer-auction", bookingId] }),
    ]);
  }

  useBookingRealtime(bookingId, () => {
    void refresh();
  });

  async function handleApprove() {
    if (busy) return;
    setBusy(true);
    try {
      const result = await approveCompletion({ data: { bookingId } });
      if (!result.ok) {
        toast.error(result.error ?? "Could not approve");
        return;
      }
      toast.success(
        result.paymentRelease === "released"
          ? "Job approved — the protected Partner payment has been released."
          : "Job approved. The protected payout needs follow-up before it can be released.",
      );
      if (result.paymentReleaseError) {
        toast.warning("Your completion approval is saved. Shields will keep the payout issue visible for follow-up.");
      }
      await refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not approve completed work");
    } finally {
      setBusy(false);
    }
  }

  if (isLoading) {
    return <div className="flex min-h-screen items-center justify-center text-muted-foreground">Loading…</div>;
  }
  if (error || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-muted-foreground">This booking could not be found.</p>
        <Link to="/bookings" className="font-semibold text-primary underline">Back to my bookings</Link>
      </div>
    );
  }

  const { booking, bids, events, quotePartnerIds, media } = data;
  const negotiatingPartners = [...new Set([
    ...quotePartnerIds,
    ...bids.map((bid) => bid.partner_id),
  ])];

  return (
    <div className="min-h-screen bg-[#f7f9fc] pb-24">
      <AppHeader workspace="customer" />
      <main className="section-pad mx-auto max-w-3xl py-6 sm:py-8">
        <Link to="/bookings" className="inline-flex items-center gap-2 text-sm font-bold text-[#66758d]">
          <ArrowLeft className="h-4 w-4" aria-hidden /> Activity
        </Link>

        {["matching", "manual_matching"].includes(booking.status) ? (
          <section className="mt-5 rounded-[1.75rem] border border-[#e1e8f0] bg-white p-7 text-center shadow-sm">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#ecf8f2]">
              <ShieldsLogo showWordmark={false} />
            </div>
            <h1 className="mt-5 text-3xl font-extrabold text-[#061b3a]">Your job is live!</h1>
            <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-[#66758d]">
              We’ll notify you as suitable Partners respond. You choose the real GBP price and who you want to use.
            </p>
            <div className="mt-6 grid gap-3">
              <a href="#job-details" className="flex min-h-12 items-center justify-center rounded-xl bg-[linear-gradient(180deg,#f2cf73,#d8a82f)] px-4 text-sm font-extrabold text-[#061b3a]">View My Job</a>
              <Link to="/home" className="flex min-h-12 items-center justify-center rounded-xl border border-[#d8e1eb] bg-white px-4 text-sm font-bold text-[#061b3a]">Explore My Home</Link>
            </div>
          </section>
        ) : ["completed","closed"].includes(booking.status) ? (
          <section className="mt-5 rounded-[1.75rem] border border-[#e1e8f0] bg-white p-7 text-center shadow-sm">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#ecf8f2] text-[#24875d]">
              <CheckCircle2 className="h-8 w-8" />
            </span>
            <p className="mt-4 text-xs font-extrabold uppercase tracking-[0.14em] text-[#c99522]">Job Complete</p>
            <h1 className="mt-1 text-3xl font-extrabold text-[#061b3a]">All done!</h1>
            <p className="mt-2 text-sm text-[#66758d]">
              {(booking.services as { name?: string } | null)?.name ?? "Service"} · {booking.reference}
            </p>
            <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-[#66758d]">
              Your completed work now stays in My Home and your service history.
            </p>
          </section>
        ) : (
          <section className="mt-5 rounded-[1.75rem] border border-[#e1e8f0] bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#c99522]">
                  {booking.status === "bidding" ? "Your Quotes" : "Your Job"}
                </p>
                <h1 className="mt-1 text-2xl font-extrabold text-[#061b3a]">
                  {booking.status === "bidding"
                    ? `${negotiatingPartners.length} quote${negotiatingPartners.length === 1 ? "" : "s"} received`
                    : ["assigned","scheduled","in_progress"].includes(booking.status)
                      ? "Your professional is on the way"
                      : (booking.services as { name?: string } | null)?.name ?? "Service"}
                </h1>
                <p className="mt-1 text-sm text-[#66758d]">
                  {(booking.services as { name?: string } | null)?.name ?? "Service"} · {booking.reference} · {booking.postcode}
                </p>
              </div>
              <StatusPill status={booking.status} label={bookingStatusLabel(booking.status)} />
            </div>
            <JobJourney status={booking.status} />
          </section>
        )}

        {["assigned", "payment_pending", "payment_failed"].includes(booking.status) && booking.agreed_price_pence != null && (
          <section className="mt-6 border border-primary/40 bg-primary/5 p-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex gap-3"><CreditCard className="h-5 w-5 shrink-0 text-primary" aria-hidden /><div><p className="technical-label">AGREED GBP PRICE · {gbp(booking.agreed_price_pence)}</p><h2 className="mt-1 font-display text-lg font-bold">Protect your booking payment</h2><p className="mt-1 text-sm text-muted-foreground">The separately disclosed Customer total is shown before Stripe Checkout. Shields Credits are not part of this payment.</p></div></div>
              <Link to="/bookings/$bookingId/payment" params={{ bookingId }} className="inline-flex min-h-12 items-center justify-center border border-primary/60 bg-primary px-4 text-sm font-extrabold text-primary-foreground">Continue to protected payment</Link>
            </div>
          </section>
        )}
        {booking.status === "payment_protected" && (
          <section className="mt-6 border border-success/40 bg-success/5 p-5">
            <div className="flex gap-3"><ShieldCheck className="h-5 w-5 text-success" aria-hidden /><div><p className="technical-label">PAYMENT PROTECTED</p><h2 className="mt-1 font-display text-lg font-bold">Partner transfer pending completion</h2><p className="mt-1 text-sm text-muted-foreground">Your job payment has been confirmed. Release requires the configured completion conditions.</p></div></div>
          </section>
        )}

        {booking.status === "awaiting_customer_approval" && (
          <div className="card-surface mt-6 border-primary/40 p-6">
            <h2 className="font-display text-lg font-bold">Your partner says the job is done</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Review the work and any evidence photos below, then approve completion or report an issue.
            </p>
            {booking.completion_notes && (
              <div className="mt-4 rounded-lg bg-muted p-4">
                <h3 className="text-sm font-bold">Partner's completion notes</h3>
                <p className="mt-1 whitespace-pre-wrap text-sm">{booking.completion_notes}</p>
              </div>
            )}
            <Button onClick={handleApprove} disabled={busy} className="mt-4 min-h-12 font-bold">
              <CheckCircle2 className="mr-2 h-4 w-4" aria-hidden /> Approve completed work &amp; release payment
            </Button>
          </div>
        )}

        {["assigned", "payment_protected", "scheduled", "on_the_way", "arrived", "in_progress", "awaiting_customer_approval", "rework"].includes(booking.status) && (
          <section className="mt-5 grid grid-cols-2 gap-3">
            <Link
              to="/messages/$bookingId"
              params={{ bookingId }}
              className="flex min-h-12 items-center justify-center rounded-xl border border-[#d8e1eb] bg-white px-4 text-sm font-extrabold text-[#061b3a]"
            >
              Message
            </Link>
            <a
              href="#job-details"
              className="flex min-h-12 items-center justify-center rounded-xl bg-[linear-gradient(180deg,#f2cf73,#d8a82f)] px-4 text-sm font-extrabold text-[#061b3a]"
            >
              View Details
            </a>
          </section>
        )}

        {["assigned", "payment_protected", "scheduled", "on_the_way", "arrived", "in_progress", "awaiting_customer_approval", "rework"].includes(
          booking.status,
        ) && (
          <ScheduleSection
            bookingId={bookingId}
            userId={user.id}
            scheduledDate={booking.scheduled_date}
            scheduledWindow={booking.scheduled_window}
          />
        )}

        <JobLocationMap postcode={booking.postcode} precise />

        {media.length > 0 && (
          <section className="card-surface mt-6 p-6">
            <h2 className="font-display text-lg font-bold">Photos & video</h2>
            <p className="mt-1 text-sm text-muted-foreground">Files attached to this Shields job.</p>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {media.map((item: { id: string; kind: string; url: string }) => (
                item.kind === "video" ? (
                  <video key={item.id} controls preload="metadata" className="aspect-square w-full rounded-xl border border-border bg-black object-cover">
                    <source src={item.url} />
                  </video>
                ) : (
                  <a key={item.id} href={item.url} target="_blank" rel="noreferrer" className="overflow-hidden rounded-xl border border-border">
                    <img src={item.url} alt="Customer job evidence" className="aspect-square w-full object-cover" loading="lazy" />
                  </a>
                )
              ))}
            </div>
          </section>
        )}

        <section id="job-details" className="card-surface mt-6 p-6">
          <h2 className="font-display text-lg font-bold">Job details</h2>
          <p className="mt-3 whitespace-pre-wrap text-sm">{booking.description}</p>
          <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
            {booking.property_type && (
              <div><dt className="font-semibold text-muted-foreground">Property</dt><dd className="capitalize">{booking.property_type}</dd></div>
            )}
            {booking.maintenance_condition && (
              <div><dt className="font-semibold text-muted-foreground">Condition</dt><dd>{booking.maintenance_condition}</dd></div>
            )}
            {booking.preferred_date && (
              <div><dt className="font-semibold text-muted-foreground">Preferred</dt><dd>{ukDate(booking.preferred_date)}{booking.preferred_window ? ` · ${booking.preferred_window}` : ""}</dd></div>
            )}
            {booking.agreed_price_pence != null && (
              <div><dt className="font-semibold text-muted-foreground">Agreed price</dt><dd className="font-bold">{gbp(booking.agreed_price_pence)}</dd></div>
            )}
          </dl>
        </section>

        {negotiatingPartners.length > 0 && booking.status === "bidding" && booking.price_agreed_at == null && (
          <section id="quote-review" className="mt-6 space-y-4 scroll-mt-24">
            <div>
              <p className="technical-label">PARTNER QUOTES</p>
              <h2 className="mt-1 font-display text-xl font-bold">Agree the real price in pounds</h2>
              <p className="mt-1 text-sm text-muted-foreground">You choose and agree the real GBP price. Shields then handles the Partner selection stage behind the scenes.</p>
            </div>
            {negotiatingPartners.map((partnerId) => (
              <QuoteNegotiation
                key={partnerId}
                bookingId={bookingId}
                partnerId={partnerId}
                role="customer"
                guidePence={null}
                partnerName="this Partner"
              />
            ))}
          </section>
        )}

        {["matching", "manual_matching"].includes(booking.status) && (
          <section className="mt-8 border border-border bg-card/65 p-5">
            <h2 className="flex items-center gap-2 font-display text-lg font-bold"><Gavel className="h-5 w-5 text-primary" aria-hidden /> Matching Partners</h2>
            <p className="mt-4 text-sm leading-6 text-muted-foreground">{booking.status === "manual_matching" ? "The matching team is reviewing this job manually." : "Shields is dispatching your privacy-safe requirement to suitable Partners."}</p>
          </section>
        )}

        {["completed", "closed"].includes(booking.status) && <ReviewSection bookingId={bookingId} />}

        <section className="mt-8">
          <h2 className="font-display text-lg font-bold">Timeline</h2>
          <ol className="mt-4 space-y-3 border-l-2 border-border pl-4">
            {events.map((e) => {
              const d = (e.detail ?? {}) as Record<string, any>;
              const matched =
                e.event_type === "offers_dispatched"
                  ? `Sent to ${d['count'] ?? 0} best-matched partner${d['count'] === 1 ? "" : "s"} nearby${
                      d['wave'] ? ` (round ${d['wave']} of 3)` : ""
                    }${d['radius_stretched'] ? " · search area widened" : ""}`
                  : null;
              return (
                <li key={e.id} className="text-sm">
                  <span className="font-semibold capitalize">{e.event_type.replaceAll("_", " ")}</span>
                  <span className="ml-2 text-xs text-muted-foreground">{ukDateTime(e.created_at)}</span>
                  {matched && <p className="text-xs text-muted-foreground">{matched}</p>}
                </li>
              );
            })}
          </ol>
        </section>

        <section className="mt-10 border-t border-border pt-6">
          {!issueOpen ? (
            <button
              onClick={() => setIssueOpen(true)}
              className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-destructive"
            >
              <Flag className="h-4 w-4" aria-hidden /> Report a problem with this booking
            </button>
          ) : (
            <IssueForm
              bookingId={bookingId}
              onDone={async () => {
                setIssueOpen(false);
                await refresh();
              }}
            />
          )}
        </section>
      </main>

      {booking.status === "bidding" && (
        <StickyJobActionBar
          compact
          label="Partner selection"
          value={`${liveAuction?.activeBids ?? 0} Partner option${(liveAuction?.activeBids ?? 0) === 1 ? "" : "s"}`}
          note={booking.price_agreed_at ? "Your agreed GBP price is fixed." : "Review Partner quotes and agree the real GBP price."}
        >
          {!booking.price_agreed_at && negotiatingPartners.length > 0 ? (
            <a href="#quote-review" className="inline-flex min-h-11 items-center justify-center border border-primary bg-primary px-4 text-sm font-extrabold text-primary-foreground">Review quotes</a>
          ) : (
            <span className="inline-flex min-h-11 items-center justify-center border border-border bg-background px-4 text-sm font-bold text-muted-foreground">Live updates on</span>
          )}
        </StickyJobActionBar>
      )}
    </div>
  );
}

function IssueForm({ bookingId, onDone }: { bookingId: string; onDone: () => Promise<void> }) {
  const [category, setCategory] = useState("work_quality");
  const [description, setDescription] = useState("");
  const [outcome, setOutcome] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const result = await reportIssue({
      data: { bookingId, category, description, requestedOutcome: outcome || undefined },
    });
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? "Could not submit");
      return;
    }
    toast.success("Issue reported — our team will review it");
    await onDone();
  }

  return (
    <form onSubmit={submit} className="card-surface space-y-4 p-6">
      <h3 className="font-display font-bold">Report an issue</h3>
      <p className="text-sm text-muted-foreground">
        A member of the Shields team reviews every report personally.
      </p>
      {error && (
        <p role="alert" className="rounded-lg bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">{error}</p>
      )}
      <div>
        <Label htmlFor="category">Category</Label>
        <select
          id="category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="mt-1.5 min-h-12 w-full rounded-lg border border-input bg-background px-3 text-sm"
        >
          <option value="work_quality">Work quality</option>
          <option value="no_show">Partner didn't show</option>
          <option value="damage">Property damage</option>
          <option value="billing">Price or payment</option>
          <option value="conduct">Conduct</option>
          <option value="other">Something else</option>
        </select>
      </div>
      <div>
        <Label htmlFor="issueDesc">What happened?</Label>
        <Textarea
          id="issueDesc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          minLength={10}
          maxLength={4000}
          required
          className="mt-1.5"
        />
      </div>
      <div>
        <Label htmlFor="outcome">What outcome would you like? (optional)</Label>
        <Input
          id="outcome"
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
          maxLength={500}
          placeholder="e.g. rework, partial refund"
          className="mt-1.5 min-h-12"
        />
      </div>
      <Button type="submit" disabled={busy} className="min-h-12 font-bold">
        {busy ? "Sending…" : "Submit report"}
      </Button>
    </form>
  );
}

function ReviewSection({ bookingId }: { bookingId: string }) {
  const queryClient = useQueryClient();
  const { data: review, isLoading } = useQuery({
    queryKey: ["review", bookingId],
    queryFn: () => getBookingReview({ data: { bookingId } }),
  });
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const { data: wouldHireAgain } = useQuery({ queryKey: ["would-hire-again", bookingId], queryFn: () => getWouldHireAgain({ data: { bookingId } }) });
  const { data: marketingConsent } = useQuery({ queryKey: ["partner-marketing-consent", bookingId], queryFn: () => getCustomerPartnerMarketingConsent({ data: { bookingId } }) });
  const [hireAgain, setHireAgain] = useState<boolean | null>(null);
  const [emailMarketing, setEmailMarketing] = useState<boolean | null>(null);
  const [smsMarketing, setSmsMarketing] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const photoPaths: string[] = [];
      for (const file of files) {
        photoPaths.push(await uploadPhoto("review-photos", file));
      }
      const result = await submitReview({ data: { bookingId, rating, body, photoPaths } });
      if (!result.ok) {
        toast.error(result.error ?? "Could not submit your review");
        return;
      }
      const repeatChoice = hireAgain ?? wouldHireAgain ?? false;
      const repeatResult = await setWouldHireAgain({ data: { bookingId, wantsAgain: repeatChoice } });
      if (!repeatResult.ok) {
        toast.error("Your review was published, but your repeat-Partner preference could not be saved.");
      }
      const emailAllowed = emailMarketing ?? marketingConsent?.emailAllowed ?? false;
      const smsAllowed = smsMarketing ?? marketingConsent?.smsAllowed ?? false;
      const consentResult = await setCustomerPartnerMarketingConsent({
        data: { bookingId, emailAllowed, smsAllowed },
      });
      if (!consentResult.ok) {
        toast.error("Your review was published, but your marketing preferences could not be updated.");
      }
      toast.success("Thank you — your review is live on the Partner's profile");
      await queryClient.invalidateQueries({ queryKey: ["review", bookingId] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mt-6 rounded-[1.75rem] border border-[#e1e8f0] bg-white p-6 shadow-sm">
      <h2 className="text-xl font-extrabold text-[#061b3a]">How did it go?</h2>
      {isLoading ? (
        <p className="mt-3 text-sm text-muted-foreground">Loading…</p>
      ) : review ? (
        <div className="mt-3">
          <span className="inline-flex items-center gap-0.5" aria-label={`${review.rating} out of 5 stars`}>
            {[1, 2, 3, 4, 5].map((n) => (
              <Star
                key={n}
                className={`h-4 w-4 ${n <= review.rating ? "fill-warning text-warning" : "text-muted-foreground/30"}`}
                aria-hidden
              />
            ))}
          </span>
          <p className="mt-2 whitespace-pre-wrap text-sm">{review.body}</p>
          {review.photos.length > 0 && (
            <ul className="mt-3 flex flex-wrap gap-2">
              {review.photos.map((url, i) =>
                url ? (
                  <li key={i}>
                    <img src={url} alt={`Your review photo ${i + 1}`} className="h-24 w-24 rounded-lg object-cover" />
                  </li>
                ) : null,
              )}
            </ul>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            Published on the partner's public profile with your first name only.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-4 space-y-4">
          <p className="text-sm text-muted-foreground">
            How was the work? Your review helps neighbours choose — and keeps partners accountable.
          </p>
          <div>
            <Label>Rating</Label>
            <div className="mt-1.5 flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  aria-label={`${n} star${n > 1 ? "s" : ""}`}
                  aria-pressed={rating === n}
                  className="rounded p-1"
                >
                  <Star
                    className={`h-7 w-7 ${n <= rating ? "fill-warning text-warning" : "text-muted-foreground/30"}`}
                    aria-hidden
                  />
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="reviewBody">What happened?</Label>
            <Textarea
              id="reviewBody"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              minLength={10}
              maxLength={2000}
              required
              placeholder="Tell us about your experience (optional details)"
              className="mt-1.5"
            />
          </div>
          <button type="button" onClick={() => setHireAgain(!(hireAgain ?? wouldHireAgain ?? false))} className={"repeat-partner-choice "+((hireAgain ?? wouldHireAgain)?"is-selected":"")} aria-pressed={Boolean(hireAgain ?? wouldHireAgain)}>
            <Heart aria-hidden /><span><strong>I’d like to have this Partner again</strong><small>For suitable future jobs, Shields can prioritise this Partner. Other eligible Partners can still receive the opportunity.</small></span>
          </button>
          <fieldset className="rounded-xl border border-border p-4">
            <legend className="px-1 text-sm font-bold">Optional contact preferences</legend>
            <p className="text-xs text-muted-foreground">These are separate from your review and repeat-Partner choice. Leave them off if you do not want marketing contact from this business.</p>
            <label className="mt-3 flex items-start gap-3 text-sm">
              <input type="checkbox" checked={emailMarketing ?? marketingConsent?.emailAllowed ?? false} onChange={(e)=>setEmailMarketing(e.target.checked)} className="mt-0.5 h-4 w-4 accent-primary"/>
              <span>Allow this business to send occasional marketing or service offers by email.</span>
            </label>
            <label className="mt-3 flex items-start gap-3 text-sm">
              <input type="checkbox" checked={smsMarketing ?? marketingConsent?.smsAllowed ?? false} onChange={(e)=>setSmsMarketing(e.target.checked)} className="mt-0.5 h-4 w-4 accent-primary"/>
              <span>Allow this business to send occasional marketing or service offers by SMS.</span>
            </label>
          </fieldset>
          <div>
            <Label htmlFor="reviewPhotos">Photos of the finished work (optional, up to 3)</Label>
            <Input
              id="reviewPhotos"
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files ?? []).slice(0, 3))}
              className="mt-1.5"
            />
          </div>
          <Button type="submit" disabled={busy} className="min-h-12 bg-[#e1b64f] font-extrabold text-[#061b3a] hover:bg-[#d8a82f]">
            <Camera className="mr-2 h-4 w-4" aria-hidden />
            {busy ? "Publishing…" : "Submit Review"}
          </Button>
        </form>
      )}
    </section>
  );
}